// WHERE THE JUST-IN-TIME VISIT JOIN LINK IS CACHED, and the one place in the repo that spells it.
//
// ⚠ TWO SURFACES ASK FOR THIS LINK AT ONCE, AND THEY MUST NOT EACH FETCH IT. `@internal/telemedicine`'s
// `HomeAppointmentsTile` (via `useAppointmentTiming` → its own `useJoinLink`) and
// `@internal/consumer`'s plan card (via its own `useJoinLink`) are SIBLINGS inside the consumer
// home's sidebar layout (`src/pages/consumer/home/page.tsx`), they go live at the same imminence
// crossing, and there is exactly ONE `QueryClientProvider` in the app (`src/main.tsx`). Under two
// different keys that meant two reads of transient PHI and two per-appointment fan-outs upstream
// for ONE visit; under this one it is one request and one answer, the same argument
// `APPOINTMENTS_QUERY_KEY` makes for the appointment list itself (see `./appointments.ts`).
//
// ⚠ THE TWO HOOKS STAY SEPARATE, and only the CACHE ENTRY is shared. They hold different tRPC
// clients on purpose — the consumer package owns its own to keep its one-way dependency — and they
// report their absent case differently to their own callers (`undefined` there, `null` here). What
// they cannot differ on is WHICH READ THEY ARE: both call `telemedicine.getAppointmentJoinLink`
// with the same argument — since MEM-879 they read the same `getAppointments` list, so both are
// holding scheduling-api's `appointment.id` off the same rows.
//
// ⚠ REACHED BY SUBPATH (`@internal/constants/telemedicine/join-link`) and NOT re-exported from
// `../index.ts`, for the same reason `telemedicine/imminence.ts` is not: the root re-exports
// `united-states/states.ts`, which imports `react-intl`. Keep this module import-free.

/**
 * The react-query key for one appointment's join link.
 *
 * A builder rather than a prefix both callers append to, so the shape of the key — and not just its
 * first element — has one definition. `appointmentId` is threaded even when the caller has none
 * (the empty string, alongside a `false` `enabled`): react-query needs a key for a disabled query,
 * and both hooks refuse to fetch on an empty id.
 */
export const joinLinkQueryKey = (appointmentId: string): readonly [string, string] => [
  "telemedicine.getAppointmentJoinLink",
  appointmentId
]

/**
 * How long one surface's answer serves the other, and the whole reason the shared key pays off.
 *
 * ⚠ THIS IS NOT A PHI LIFETIME, and it must not be read as one — `cacheTime: 0` on both hooks is,
 * and it is unaffected: the entry is dropped the instant no surface is observing it, so nothing
 * here survives a card leaving the screen. What `staleTime` decides is narrower: whether a SECOND
 * reader that arrives while the first is still showing its **Join** re-asks upstream for a link it
 * can already see. Without it the sharing buys almost nothing, because a sibling enabling a beat
 * later counts as stale and fetches again (`shouldFetchOptionally` gates on staleness).
 *
 * A minute rather than the visit's length: comfortably longer than the skew between two surfaces
 * crossing the same imminence threshold on two independent clocks, and short enough that a link
 * re-read after a member has been sitting on the page is a fresh one.
 */
export const JOIN_LINK_SHARED_STALE_MS = 60 * 1000
